import { CalendarToday, Dashboard, People, LocalHospital, Sensors, Settings, ChevronLeft, ChevronRight, Help, Support, MedicalServices, Event, EmojiEvents, Build, Description, Chat, Medication, BedroomParent } from "@mui/icons-material";
import { Badge, Box, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Avatar } from "@mui/material";
import { useNavigate, Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next"; // Aggiungi questo import
import useAxiosPrivate from './hooks/useAxiosPrivate';
import useAuth from './hooks/useAuth';
import Header from "./components/Header";
import CalendarPage from "./pages/CalendarPage"; // Importa la pagina del calendario

const Layout = ({ children }) => {
    const expandedDrawerWidth = 200;
    const collapsedDrawerWidth = 64; // Larghezza del drawer quando è contratto
    const [isExpanded, setIsExpanded] = useState(true); // Stato per tracciare se il menu è espanso
    const [mobileOpen, setMobileOpen] = useState(false); // Stato per il menu mobile
    const [tenantLogo, setTenantLogo] = useState(null);
    const drawerWidth = isExpanded ? expandedDrawerWidth : collapsedDrawerWidth;
   
    const navigate = useNavigate();
    const { t } = useTranslation("global"); // Inizializza il hook per la traduzione
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    // Carica il logo del tenant
    useEffect(() => {
        const loadTenantLogo = async () => {
            try {
                const response = await axiosPrivate.get('/tenant-settings');
                if (response.data && response.data.logo_path) {
                    setTenantLogo(response.data);
                }
            } catch (error) {
                console.error('Errore caricamento logo tenant:', error);
            }
        };
        
        loadTenantLogo();
    }, []);

    // Ottieni URL del logo tenant
    const getLogoUrl = () => {
        if (tenantLogo?.logo_path && auth?.customer_id) {
            // Usa il proxy Apache invece di accedere direttamente alla porta 4000
            const baseURL = window.location.hostname === 'localhost' 
                ? 'http://localhost:4000' 
                : '';  // URL relativo per usare il proxy Apache
            return `${baseURL}/uploads/customer_${auth.customer_id}/${tenantLogo.logo_path}`;
        }
        // Fallback al logo di default
        return `${process.env.PUBLIC_URL}/images/logo.png`;
    };

    const menuItems = [
        {
            text: t("menu.dashboard"),
            icon: <Dashboard />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}`,
        },
        {
            text: t("menu.residents"), // Usa il sistema di traduzione
            icon: <People />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/elderly`,
        },
        {
            text: t("menu.medicines"),
            icon: <Medication />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/medicines`,
        },
        {
            text: t("menu.nurses"),
            icon: <LocalHospital />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/nurses`,
        },
        {
            text: t("menu.doctors"),
            icon: <MedicalServices/>,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/doctors`,
        },
        {
            text: "Camere",
            icon: <BedroomParent/>,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/rooms`,
        },
        {
            text: t("menu.appointments"),
            icon: <Event />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/appointments`,
        },
        {
            text: t("menu.events"),
            icon: <EmojiEvents />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/events`,
        },
        {
            text: t("menu.calendar"),
            icon: <CalendarToday />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/calendar`,
        },
        {
            text: t("menu.maintenance"),
            icon: <Build />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/maintenance`,
        },
        {
            text: t("menu.documents"),
            icon: <Description />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/documents`,
        },
        {
            text: t("menu.contacts"),
            icon: <Chat />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/contacts`,
        },
        {
            text: t("menu.settings"),
            icon: <Settings />,
            path: `${process.env.REACT_APP_BASE_PATH || '/dashboard'}/settings`,
        },
    ];

    // Funzione per alternare lo stato del drawer
    const toggleDrawer = () => {
        setIsExpanded(!isExpanded);
    };

    // Funzione per gestire il menu mobile
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Contenuto del drawer (riusato per desktop e mobile)
    const drawerContent = (isMobile = false) => (
        <>
            <Toolbar>
                <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    width: "100%",
                    overflow: 'hidden'
                }}>
                    <Box
                        sx={{
                            width: 80, 
                            height: 80,
                            bgcolor: 'white',
                            border: '2px solid #e0e0e0',
                            borderRadius: '50%',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        }}
                    >
                        <img 
                            src={getLogoUrl()} 
                            alt="Logo" 
                            style={{ 
                                width: "100%", 
                                height: "100%", 
                                objectFit: "contain" 
                            }}
                            onError={(e) => {
                                e.target.src = `${process.env.PUBLIC_URL}/images/logo.png`;
                            }}
                        />
                    </Box>
                </Box>
            </Toolbar>
            <Divider />
            <List>
                {menuItems.map((item) => (
                    <ListItem
                        key={item.text}
                        disablePadding
                        onClick={() => {
                            navigate(item.path);
                            if (isMobile) setMobileOpen(false); // Chiudi drawer dopo click su mobile
                        }}
                        title={item.text}
                    >
                        <ListItemButton sx={{ px: 3 }}>
                            <ListItemIcon sx={{ minWidth: 36, mr: 3 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            {/* Header */}
            <Header drawerWidth={drawerWidth} onMenuClick={handleDrawerToggle} />

            {/* Drawer */}
            <Box
                component="nav"
                sx={{ 
                    width: { sm: drawerWidth }, 
                    flexShrink: { sm: 0 },
                    transition: 'width 0.3s ease-in-out' // Animazione durante il cambio di larghezza
                }}
                aria-label="mailbox folders"
            >
                {/* Drawer Desktop - Versione collassabile */}
                <Drawer
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": {
                            width: drawerWidth,
                            boxSizing: "border-box",
                            transition: 'width 0.3s ease-in-out',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                        },
                    }}
                    variant="permanent"
                    anchor="left"
                >
                    {isExpanded ? (
                        <>
                            {drawerContent(false)}
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                <IconButton onClick={toggleDrawer}>
                                    <ChevronLeft />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Toolbar>
                                <Box sx={{ 
                                    display: "flex", 
                                    justifyContent: "center", 
                                    width: "100%"
                                }}>
                                    <Dashboard sx={{ fontSize: 32, color: 'primary.main' }} />
                                </Box>
                            </Toolbar>
                            <Divider />
                            <List>
                                {menuItems.map((item) => (
                                    <ListItem
                                        key={item.text}
                                        disablePadding
                                        onClick={() => navigate(item.path)}
                                        title={item.text}
                                    >
                                        <ListItemButton sx={{ px: 2, justifyContent: 'center' }}>
                                            <ListItemIcon sx={{ minWidth: 0, mr: 'auto' }}>
                                                {item.icon}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                <IconButton onClick={toggleDrawer}>
                                    <ChevronRight />
                                </IconButton>
                            </Box>
                        </>
                    )}
                </Drawer>

                {/* Drawer Mobile - Temporary (si apre/chiude con hamburger) */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Migliora performance su mobile
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: expandedDrawerWidth 
                        },
                    }}
                >
                    {drawerContent(true)}
                </Drawer>
            </Box>

            {/* Contenuto principale */}
            <Box 
                component="main" 
                sx={{ 
                    flexGrow: 1, 
                    p: 0, // Rimuove qualsiasi padding interno
                     // Usa solo la larghezza del menu senza aggiungere margini extra
                    width: `calc(100% - ${drawerWidth}px)`, // Aggiorna la larghezza per compensare il menu
                    transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out' // Mantiene l'animazione fluida
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

export default Layout;